import { PdfDocumentBuilder, type EmpresaInfo, type DocumentConfig } from './pdfDocumentEngine';

interface MandatoData {
  id?: string;
  nombre_titular: string;
  direccion_calle: string;
  direccion_numero: string;
  codigo_postal: string;
  ciudad: string;
  provincia: string;
  nif_dni: string;
  iban: string;
  nombre_acreedor?: string;
  identificador_acreedor?: string;
  fecha_firma?: string;
  firma_digital?: string;
  created_at?: string;
}

export const generateMandatoPDF = async (
  mandatoData: MandatoData,
  empresa?: EmpresaInfo | null,
  docConfig?: Partial<DocumentConfig>,
): Promise<void> => {
  const builder = await PdfDocumentBuilder.create(empresa, docConfig);

  // Header
  builder.addHeader('Mandato SEPA', 'Autorización de Domiciliación Bancaria');

  // Reference
  if (mandatoData.id) {
    builder.addField('Referencia', mandatoData.id.substring(0, 8).toUpperCase());
    builder.addSpacer(3);
  }

  // Datos del titular
  builder.addSection('Datos del Titular de la Cuenta');
  builder.addFieldsGrid([
    { label: 'Nombre completo', value: mandatoData.nombre_titular },
    { label: 'NIF/DNI', value: mandatoData.nif_dni },
    { label: 'Dirección', value: `${mandatoData.direccion_calle} ${mandatoData.direccion_numero}` },
    { label: 'Localidad', value: `${mandatoData.codigo_postal} ${mandatoData.ciudad} (${mandatoData.provincia})` },
  ], 2);

  // Datos bancarios
  builder.addSection('Datos Bancarios');
  builder.addField('IBAN', mandatoData.iban);
  builder.addSpacer(3);

  // Datos del acreedor
  builder.addSection('Datos del Acreedor');
  builder.addField('Nombre', mandatoData.nombre_acreedor || empresa?.nombre || 'Sin especificar');
  if (mandatoData.identificador_acreedor) {
    builder.addField('Identificador', mandatoData.identificador_acreedor);
  }
  builder.addSpacer(3);

  // Autorización
  builder.addSection('Autorización');
  const acreedor = mandatoData.nombre_acreedor || empresa?.nombre || 'el acreedor';
  const legalText = docConfig?.textoLegal ||
    `Mediante la firma de este mandato, autorizo a ${acreedor} a enviar instrucciones a mi banco para adeudar mi cuenta y a mi banco para efectuar adeudos en mi cuenta de conformidad con las instrucciones de ${acreedor}.\n\nComo parte de mis derechos, estoy legitimado al reembolso por mi banco bajo los términos y condiciones del contrato suscrito con la entidad bancaria. La solicitud de reembolso deberá efectuarse dentro de las ocho semanas que siguen a la fecha de adeudo en cuenta.\n\nPuedo obtener información adicional sobre mis derechos en mi entidad bancaria.`;
  builder.addLegalText(legalText);

  // Fecha
  const fechaFirma = mandatoData.fecha_firma
    ? new Date(mandatoData.fecha_firma).toLocaleDateString('es-ES')
    : new Date().toLocaleDateString('es-ES');
  builder.addField('Fecha de firma', fechaFirma);
  builder.addSpacer(3);

  // Firma
  builder.addSignatureBlock([
    {
      label: 'Firma del Titular',
      imageData: mandatoData.firma_digital || null,
      nombre: mandatoData.nombre_titular,
    },
    { label: 'Sello de la empresa', esEmpresa: true },
  ]);

  // Save
  const fileName = `mandato_sepa_${mandatoData.nombre_titular.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  builder.save(fileName);
};
